import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "@/lib/api";
import { exportLeadsCsv, exportLeadsExcel, fetchAllLeadsForExport } from "@/lib/exportLeads";
import { IconEye, IconTrash, IconWhats } from "@/components/admin/Icons";

type LeadRow = {
  id: number;
  nome: string;
  email: string;
  whatsapp: string;
  status:
    | "novo"
    | "contatado"
    | "agendado"
    | "compareceu"
    | "no_show"
    | "convertido"
    | "descartado";
  prioridade: "baixa" | "media" | "alta";
  created_at: string;
  updated_at: string;
};

type ListResponse = {
  leads: LeadRow[];
  total: number;
  page: number;
  pageSize: number;
};

const STATUS_LABEL: Record<LeadRow["status"], string> = {
  novo: "Novo",
  contatado: "Contatado",
  agendado: "Agendado",
  compareceu: "Compareceu",
  no_show: "No-show",
  convertido: "Convertido",
  descartado: "Descartado",
};

function fmtDateTime(s: string): string {
  const d = new Date(s.replace(" ", "T"));
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function waUrl(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  const prefixed = digits.startsWith("55") ? digits : `55${digits}`;
  return `https://wa.me/${prefixed}`;
}

export default function AdminLeads() {
  const navigate = useNavigate();
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [prioridade, setPrioridade] = useState("");
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState<"csv" | "excel" | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (status) params.set("status", status);
    if (prioridade) params.set("prioridade", prioridade);
    params.set("page", String(page));
    params.set("pageSize", "20");

    apiFetch<ListResponse>(`/admin/leads?${params.toString()}`, { auth: "admin" })
      .then(setData)
      .catch(() => setError("Falha ao carregar leads."))
      .finally(() => setLoading(false));
  }, [search, status, prioridade, page, refreshKey]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  const handleExport = async (format: "csv" | "excel") => {
    setExporting(format);
    setError(null);
    try {
      const rows = await fetchAllLeadsForExport({ search, status, prioridade });
      if (format === "csv") exportLeadsCsv(rows);
      else exportLeadsExcel(rows);
    } catch {
      setError("Falha ao exportar leads.");
    } finally {
      setExporting(null);
    }
  };

  const deleteLead = async (lead: LeadRow) => {
    const ok = window.confirm(`Excluir definitivamente o lead "${lead.nome}"? Essa acao nao pode ser desfeita.`);
    if (!ok) return;

    setDeletingId(lead.id);
    setError(null);
    try {
      await apiFetch(`/admin/leads/${lead.id}`, { method: "DELETE", auth: "admin" });
      if (data?.leads.length === 1 && page > 1) setPage((p) => Math.max(1, p - 1));
      else setRefreshKey((k) => k + 1);
    } catch {
      setError("Falha ao excluir lead.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <div className="adm-topbar">
        <div>
          <h1 className="adm-title">Leads</h1>
          <p className="adm-sub">{data?.total ?? 0} cadastros no total</p>
        </div>
        <div className="adm-topactions">
          <button
            type="button"
            className="btn btn-ghost adm-export-btn"
            disabled={!!exporting || loading || !data?.total}
            onClick={() => handleExport("csv")}
          >
            {exporting === "csv" ? "Exportando..." : "CSV"}
          </button>
          <button
            type="button"
            className="btn btn-primary adm-export-btn"
            disabled={!!exporting || loading || !data?.total}
            onClick={() => handleExport("excel")}
          >
            {exporting === "excel" ? "Exportando..." : "Excel"}
          </button>
        </div>
      </div>

      {error && <div className="adm-login-error">{error}</div>}

      <div className="adm-panel">
        <div className="adm-filters">
          <input
            className="adm-input"
            placeholder="Buscar por nome, e-mail, WhatsApp…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={{ minWidth: 280 }}
          />
          <select
            className="adm-select"
            value={status}
            onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          >
            <option value="">Todos os status</option>
            {Object.entries(STATUS_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <select
            className="adm-select"
            value={prioridade}
            onChange={(e) => { setPrioridade(e.target.value); setPage(1); }}
          >
            <option value="">Todas as prioridades</option>
            <option value="alta">Alta</option>
            <option value="media">Média</option>
            <option value="baixa">Baixa</option>
          </select>
        </div>

        <table className="adm-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>E-mail</th>
              <th>WhatsApp</th>
              <th>Status</th>
              <th>Criado</th>
              <th style={{ width: 132 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} style={{ color: "var(--ink-3)" }}>Carregando…</td></tr>
            )}
            {!loading && data?.leads.length === 0 && (
              <tr><td colSpan={6} style={{ color: "var(--ink-3)" }}>Nenhum lead encontrado.</td></tr>
            )}
            {!loading && data?.leads.map((l) => (
              <tr key={l.id} onClick={() => navigate(`/admin/leads/${l.id}`)}>
                <td data-label="Nome" style={{ fontWeight: 600 }}>{l.nome}</td>
                <td data-label="E-mail">{l.email}</td>
                <td data-label="WhatsApp">{l.whatsapp}</td>
                <td data-label="Status"><span className={`adm-pill s-${l.status}`}>{STATUS_LABEL[l.status]}</span></td>
                <td data-label="Criado" style={{ color: "var(--ink-3)" }}>{fmtDateTime(l.created_at)}</td>
                <td data-label="Ações">
                  <div className="adm-actions-cell" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className="adm-btn-icon"
                      title="Ver detalhe"
                      onClick={() => navigate(`/admin/leads/${l.id}`)}
                    >
                      <IconEye />
                    </button>
                    <a
                      className="adm-btn-icon"
                      title="Abrir WhatsApp"
                      href={waUrl(l.whatsapp)}
                      target="_blank"
                      rel="noopener"
                    >
                      <IconWhats />
                    </a>
                    <button
                      type="button"
                      className="adm-btn-icon adm-btn-danger"
                      title="Excluir lead"
                      disabled={deletingId === l.id}
                      onClick={() => deleteLead(l)}
                    >
                      <IconTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {data && data.total > data.pageSize && (
          <div className="adm-pagination">
            <button
              type="button"
              className="btn btn-ghost"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              style={{ padding: "10px 18px", fontSize: 14 }}
            >
              ← Anterior
            </button>
            <span className="adm-pagination-label">
              Página {page} de {totalPages}
            </span>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              style={{ padding: "10px 18px", fontSize: 14 }}
            >
              Próxima →
            </button>
          </div>
        )}
      </div>
    </>
  );
}
